import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Check, LoaderCircle, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

interface ConfirmModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  onSubmit: () => Promise<void>
  loading: boolean
  title?: string
  description?: string
  buttonLabel?: string
  loadingButtonLabel?: string
  successMessageDescription?: string
  successMessageTitle?: string
  isNested?: boolean
  hideToast?: boolean
  submitButtonIcon?: React.ReactNode
  hideWarning?: boolean
  content?: React.ReactNode
  onHandleClose?: () => void
  doNotCloseOnSubmit?: boolean
  submitButtonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  cancelButtonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  /** Disables the submit button, e.g. while a required field in `content` is still empty */
  submitDisabled?: boolean
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  setOpen,
  onSubmit,
  loading,
  title = 'Confirm Action',
  description = 'Are you sure you want to proceed?',
  buttonLabel = 'Confirm',
  loadingButtonLabel = 'Processing...',
  successMessageTitle,
  isNested = false,
  hideToast = false,
  hideWarning = false,
  submitButtonIcon = <Check />,
  submitButtonVariant = 'default',
  cancelButtonVariant = 'destructive',
  content,
  onHandleClose,
  doNotCloseOnSubmit = false,
  submitDisabled = false,
}) => {
  const [confirm, setConfirm] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  const closeTriggeredRef = useRef(false)

  // Memoize the onSubmit callback to prevent infinite re-renders
  const memoizedonSubmit = useMemo(() => onSubmit, [onSubmit])

  useEffect(() => {
    if (confirm) {
      void (async () => {
        try {
          await memoizedonSubmit()
          if (!hideToast) toast.success(successMessageTitle ? successMessageTitle : 'Success')
        } catch (err: unknown) {
          if (!hideToast) {
            toast.error('Error', {
              description: err instanceof Error ? err?.message : 'Something went wrong',
            })
          }
        } finally {
          setConfirm(false)
          if (!doNotCloseOnSubmit) setOpen(false)
        }
      })()
    }
  }, [confirm])

  useEffect(() => {
    if (wasOpen && !open) {
      if (onHandleClose) onHandleClose()
      closeTriggeredRef.current = false
    }
    setWasOpen(open)
  }, [open, wasOpen, onHandleClose])

  // Keyboard shortcuts: Cmd+Enter confirms, Esc closes.
  // Registered on window in the capture phase so it runs before parent dialogs
  // (e.g. Radix) and stops them from also handling the same key.
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (!loading && !confirm) setOpen(false)
        return
      }

      if (e.key === 'Enter' && e.metaKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (!loading && !confirm && !submitDisabled) setConfirm(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, loading, confirm, submitDisabled, setOpen])

  if (!open) return null

  const modalElement = (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center p-4 animate-in fade-in duration-300',
        isNested ? 'z-[1002] bg-black/40 backdrop-blur-none' : 'z-[1000] bg-black/60 backdrop-blur-sm'
      )}
    >
      <div className='flex flex-col max-w-[420px] w-full bg-white dark:bg-card rounded-lg shadow-xl border relative overflow-hidden animate-in zoom-in-95 duration-300'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b dark:border-gray-700'>
          <div className='flex items-center gap-3'>
            <h2 className='text-lg  text-gray-900 dark:text-white'>{title || 'Confirm Action'}</h2>
          </div>
          <button
            type='button'
            className='w-8 h-8 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200'
            onClick={() => setOpen(false)}
          >
            <X className='w-4 h-4 text-gray-500 dark:text-gray-400' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          <p className='text-gray-700 text-left dark:text-gray-300 mb-2'>
            {description || 'Are you sure you want to proceed?'}
          </p>
          {content}

          {!hideWarning && (
            <div className='mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg'>
              <p className='text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2'>
                <span className='w-1.5 h-1.5 bg-amber-500 rounded-full'></span>
                This action cannot be undone.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className='px-6 pb-6 flex gap-3 justify-end border-t pt-4'>
          <Button type='button' variant={cancelButtonVariant} onClick={() => setOpen(false)} disabled={loading}>
            <X />
            Cancel
          </Button>
          <Button
            type='button'
            disabled={loading || submitDisabled}
            onClick={() => setConfirm(true)}
            variant={submitButtonVariant}
          >
            {loading ? <LoaderCircle className='animate-spin' /> : submitButtonIcon}
            {loading ? loadingButtonLabel : buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  )

  return isNested ? createPortal(modalElement, document.body) : modalElement
}

export default ConfirmModal
